import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Autocomplete,
  Chip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../../services/apiService';

export const EMPTY_ENTITY_OPTIONS = {
  types: [],
  statuses: ['Active', 'Inactive', 'Compromised', 'Under Investigation', 'Quarantined'],
  criticalities: ['Low', 'Medium', 'High', 'Critical'],
  severities: ['Low', 'Medium', 'High', 'Critical'],
};

/**
 * Turn an axios error from the entities API into a message for the user.
 * 409 -> "Entity already exists"; 400 -> joined validator messages.
 */
export const getEntityErrorMessage = (err, fallback = 'Request failed') => {
  const data = err?.response?.data;
  if (err?.response?.status === 409) return data?.message || 'Entity already exists';
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((e) => e.msg).join('; ');
  }
  return data?.message || err?.message || fallback;
};

const toForm = (entity) => ({
  type: entity?.type || '',
  name: entity?.name || '',
  identifier: entity?.identifier || '',
  description: entity?.description || '',
  owner: entity?.owner || entity?.assetInfo?.owner || '',
  location: entity?.location || '',
  criticality: entity?.criticality || 'Medium',
  status: entity?.status || 'Active',
  riskScore: entity?.riskScore === undefined || entity?.riskScore === null ? '' : String(entity.riskScore),
  ipAddress: entity?.properties?.ipAddress || '',
  hostname: entity?.properties?.hostname || '',
  operatingSystem: entity?.properties?.operatingSystem || '',
  tags: Array.isArray(entity?.tags) ? entity.tags : [],
});

/**
 * Create / edit dialog for an entity.
 *
 * Props:
 *  - open, onClose
 *  - entity: existing entity to edit (null/undefined to create)
 *  - options: result of GET /entities/options (fetched here if omitted)
 *  - onSaved(entity): called with the saved entity from the server
 */
export const EntityDialog = ({ open, onClose, entity, options: optionsProp, onSaved }) => {
  const isEdit = Boolean(entity?._id);
  const [form, setForm] = useState(toForm(null));
  const [options, setOptions] = useState(optionsProp || EMPTY_ENTITY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(toForm(entity));
      setError('');
    }
  }, [open, entity]);

  useEffect(() => {
    if (optionsProp) {
      setOptions(optionsProp);
      return;
    }
    if (!open) return;
    let cancelled = false;
    setLoadingOptions(true);
    apiService.entities.getOptions()
      .then((res) => { if (!cancelled) setOptions({ ...EMPTY_ENTITY_OPTIONS, ...res.data }); })
      .catch((err) => console.error('Failed to load entity options:', err))
      .finally(() => { if (!cancelled) setLoadingOptions(false); });
    return () => { cancelled = true; };
  }, [open, optionsProp]);

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.type || !form.name.trim() || !form.identifier.trim()) {
      setError('Type, name and identifier are required.');
      return;
    }
    if (form.riskScore !== '') {
      const n = Number(form.riskScore);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        setError('Risk score must be a whole number between 0 and 100.');
        return;
      }
    }

    const payload = {
      type: form.type,
      name: form.name.trim(),
      identifier: form.identifier.trim(),
      description: form.description.trim(),
      owner: form.owner.trim(),
      location: form.location.trim(),
      criticality: form.criticality,
      status: form.status,
      tags: form.tags,
      properties: {
        ipAddress: form.ipAddress.trim(),
        hostname: form.hostname.trim(),
        operatingSystem: form.operatingSystem.trim(),
      },
      assetInfo: { owner: form.owner.trim() },
    };
    if (form.riskScore !== '') payload.riskScore = Number(form.riskScore);
    // Drop empty optional strings so enum validators are not tripped by ''.
    ['description', 'owner', 'location'].forEach((k) => { if (!payload[k]) delete payload[k]; });
    Object.keys(payload.properties).forEach((k) => { if (!payload.properties[k]) delete payload.properties[k]; });
    if (!payload.assetInfo.owner) delete payload.assetInfo;

    try {
      setSaving(true);
      setError('');
      const response = isEdit
        ? await apiService.entities.update(entity._id, payload)
        : await apiService.entities.create(payload);
      if (onSaved) onSaved(response.data.entity);
      onClose();
    } catch (err) {
      console.error('Failed to save entity:', err);
      setError(getEntityErrorMessage(err, isEdit ? 'Failed to update entity' : 'Failed to create entity'));
    } finally {
      setSaving(false);
    }
  };

  const renderSelect = (label, field, values, disabled = false) => (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select value={form[field]} label={label} onChange={setField(field)}>
        {(values || []).map((v) => (
          <MenuItem key={v} value={v}>{v}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Entity' : 'New Entity'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loadingOptions && <CircularProgress size={20} sx={{ mb: 2 }} />}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            {renderSelect('Type', 'type', options.types, isEdit)}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Name"
              value={form.name}
              onChange={setField('name')}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Identifier"
              value={form.identifier}
              onChange={setField('identifier')}
              disabled={isEdit}
              inputProps={{ maxLength: 500, style: { fontFamily: 'monospace' } }}
              helperText={isEdit ? 'Type and identifier cannot be changed after creation' : 'Hostname, IP, account name, etc.'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={setField('description')}
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderSelect('Criticality', 'criticality', options.criticalities)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderSelect('Status', 'status', options.statuses)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Owner"
              value={form.owner}
              onChange={setField('owner')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Location"
              value={form.location}
              onChange={setField('location')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Risk Score"
              value={form.riskScore}
              onChange={setField('riskScore')}
              inputProps={{ min: 0, max: 100, step: 1 }}
              helperText="0 - 100"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Properties
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="IP Address"
              value={form.ipAddress}
              onChange={setField('ipAddress')}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Hostname"
              value={form.hostname}
              onChange={setField('hostname')}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Operating System"
              value={form.operatingSystem}
              onChange={setField('operatingSystem')}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.tags}
              onChange={(e, value) => setForm((prev) => ({ ...prev, tags: value }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Type a tag and press Enter" />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Entity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntityDialog;
